/**
 * **主题文件** —— 主题的导入 / 导出格式。
 *
 * ```jsonc
 * {
 *   "format": "node-print.theme",
 *   "version": 1,
 *   "note": "给同事的深色主题",
 *   "theme": {
 *     "id": "my-theme", "name": "我的主题", "scheme": "dark",
 *     "palette": { "accent": "#5aa9ff", "categories": { … } },
 *     "recipes": { "node": "glass" },
 *     "css": ".np-node { … }"
 *   }
 * }
 * ```
 *
 * ## ⚠️ 为什么校验要写得比数据包还细
 *
 * 主题文件是**外来数据**，而且它比其他外来数据更危险：
 *
 * * `css` 字段是**一段会被注入页面的 CSS**。虽然有 `scopeCss` 兜住作用域，
 *   但作者仍可以写 `@global` 放弃保护 —— 那是他的选择，**但界面必须
 *   在他导入之前把这件事说出来**，而不是默默执行；
 * * `tokens` 可以覆盖任何变量，包括 `--np-z-*` 这种「层级」变量。
 *   我们把它们当普通令牌处理（这正是「不受限制」的一部分），
 *   但**导入时要报数量**，让人知道这份主题动了多少东西。
 *
 * 所以解析结果里除了主题本身，还带一份**审计摘要**：动了多少令牌、
 * 有没有 `@global`、有没有 `css`。UI 拿它做二次确认。
 */
import type { ThemeDocument } from '../types/theme'
import { THEME_FORMAT_VERSION } from '../types/theme'
import { normalizeTheme, validateTheme } from './themeDoc'

export const THEME_FORMAT = 'node-print.theme'
export const THEME_FILE_VERSION = 1

export interface ThemeFile {
  format: typeof THEME_FORMAT
  version: number
  note?: string
  theme: ThemeDocument
}

/** 导入前给人看的审计摘要 */
export interface ThemeAudit {
  /** 会覆盖的令牌数量 */
  tokenCount: number
  /** 会覆盖的**层级**变量（`--np-z-*`）—— 这个最容易被滥用到界面错乱 */
  zIndexTokens: string[]
  /** 带了自定义 CSS 吗 */
  hasCss: boolean
  /** CSS 里有没有 `@global`（作者主动放弃了作用域保护） */
  hasGlobalCss: boolean
  /** 配了多少个分类色 / 端口色 */
  categoryCount: number
  socketCount: number
  /** 声明了布局（区域放置 / 密度）吗 */
  layoutKeys: string[]
}

export type ParseThemeResult =
  | { ok: true; file: ThemeFile; audit: ThemeAudit }
  | { ok: false; problems: string[] }

/** 统计一份主题「动了多少东西」 */
export function auditTheme(doc: ThemeDocument): ThemeAudit {
  const tokens = Object.keys(doc.tokens ?? {})
  const css = doc.css ?? ''
  return {
    tokenCount: tokens.length,
    zIndexTokens: tokens.filter((k) => /--np-z-/.test(k)),
    hasCss: !!css.trim(),
    hasGlobalCss: /@global\b/.test(css),
    categoryCount: Object.keys(doc.palette?.categories ?? {}).length,
    socketCount: Object.keys(doc.palette?.sockets ?? {}).length,
    layoutKeys: Object.keys(doc.layout ?? {}),
  }
}

/**
 * 解析一份主题文件。
 *
 * 策略同 `packageFile.ts`：**能在校验期挡掉的就别留到应用期**，
 * 产出是**一串可读的问题**而不是异常 —— 导入界面要把它们显示给人看。
 */
export function parseThemeFile(text: string): ParseThemeResult {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch (err) {
    return { ok: false, problems: [`不是合法的 JSON：${(err as Error).message}`] }
  }

  if (!raw || typeof raw !== 'object') {
    return { ok: false, problems: ['文件内容不是一个对象'] }
  }
  const obj = raw as Record<string, unknown>

  if (obj.format !== THEME_FORMAT) {
    return {
      ok: false,
      problems: [`\`format\` 必须是 \`${THEME_FORMAT}\`，收到 \`${String(obj.format)}\``],
    }
  }
  const version = Number(obj.version)
  if (!Number.isFinite(version) || version < 1) {
    return { ok: false, problems: ['缺少合法的 `version`'] }
  }
  if (version > THEME_FILE_VERSION) {
    return {
      ok: false,
      problems: [`文件版本 ${version} 比本版本（${THEME_FILE_VERSION}）新，请升级后再导入`],
    }
  }

  const themeRaw = obj.theme
  if (!themeRaw || typeof themeRaw !== 'object') {
    return { ok: false, problems: ['缺少 `theme` 字段'] }
  }
  const t = themeRaw as Record<string, unknown>

  const problems: string[] = []
  if (typeof t.id !== 'string' || !t.id.trim()) problems.push('`theme.id` 必须是非空字符串')
  if (typeof t.name !== 'string' || !t.name.trim()) problems.push('`theme.name` 必须是非空字符串')
  if (t.scheme != null && t.scheme !== 'dark' && t.scheme !== 'light') {
    problems.push('`theme.scheme` 只能是 `dark` 或 `light`')
  }
  if (t.css != null && typeof t.css !== 'string') problems.push('`theme.css` 必须是字符串')
  if (t.tokens != null && (typeof t.tokens !== 'object' || Array.isArray(t.tokens))) {
    problems.push('`theme.tokens` 必须是对象')
  }
  /* 函数、Symbol 之类塞不进 JSON，但原型污染类的键要先挡掉 */
  if (Object.prototype.hasOwnProperty.call(t, '__proto__')) {
    problems.push('`theme` 里有非法的 `__proto__` 键')
  }
  if (problems.length) return { ok: false, problems }

  const doc = normalizeTheme({
    ...(t as unknown as ThemeDocument),
    version: THEME_FORMAT_VERSION,
  })
  problems.push(...validateTheme(doc))
  if (problems.length) return { ok: false, problems }

  return {
    ok: true,
    file: { format: THEME_FORMAT, version: THEME_FILE_VERSION, note: obj.note as string | undefined, theme: doc },
    audit: auditTheme(doc),
  }
}

/** 导出成文件文本（带缩进，方便人直接改） */
export function serializeThemeFile(doc: ThemeDocument, note?: string): string {
  const file: ThemeFile = {
    format: THEME_FORMAT,
    version: THEME_FILE_VERSION,
    ...(note ? { note } : {}),
    theme: normalizeTheme(doc),
  }
  return `${JSON.stringify(file, null, 2)}\n`
}

/** 触发浏览器下载（把主题存成文件） */
export function downloadThemeFile(doc: ThemeDocument, note?: string): void {
  if (typeof document === 'undefined') return
  const blob = new Blob([serializeThemeFile(doc, note)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${doc.id}.nptheme.json`
  a.click()
  URL.revokeObjectURL(url)
}
